import { apiClient } from '../../../app/api/apiClient';
import { getTenantDomains } from './tenant-domains.api';

export interface TenantApiResponse {
  success: boolean;
  tenants: Array<{
    id: number;
    name: string;
    slug: string;
    status: 'active' | 'inactive' | 'suspended';
    metadata?: Record<string, unknown>;
    created_at?: string | null;
    updated_at?: string | null;
    representative_mobile?: string | null;
  }>;
  meta: {
    page: number;
    limit: number;
    total: number;
  };
}

export interface TenantsPage {
  tenants: TenantApiResponse['tenants'];
  meta: TenantApiResponse['meta'];
}

export async function getTenants(page = 1, pageSize = 20, search = ''): Promise<TenantsPage> {
  const response = await apiClient.get('/api/tenants', {
    query: {
      page,
      page_size: pageSize,
      ...(search.trim() ? { search: search.trim() } : {}),
    },
  });
  const data = response.data as TenantApiResponse;

  const tenants = await Promise.all(
    data.tenants.map(async (tenant) => {
      const domains = await getTenantDomains(tenant.id);
      const primaryDomain = domains.find((domain) => domain.is_primary) ?? null;
      return { ...tenant, primary_domain: primaryDomain?.domain ?? null };
    }),
  );

  return { tenants, meta: data.meta };
}


export async function getTenant(id: string) {
  const response = await apiClient.get(`/api/tenants/${id}`);

  return (response.data as {
    success: boolean;
    tenant: {
      id: number;
      name: string;
      slug: string;
      status: 'active' | 'inactive' | 'suspended';
      metadata?: Record<string, unknown>;
      created_at?: string | null;
      updated_at?: string | null;
      representative_mobile?: string | null;
    };
  }).tenant;
}


export interface CreateTenantPayload {
  name: string;
  slug: string;
  representative: {
    full_name: string;
    mobile: string;
    password: string;
  };
}

export interface CreateTenantApiResponse {
  success: boolean;
  tenant: {
    id: number;
    name: string;
    slug: string;
    status: 'active' | 'inactive' | 'suspended';
    metadata?: Record<string, unknown>;
    created_at?: string | null;
    updated_at?: string | null;
  };
  representative: {
    id: number;
    tenant_id: number;
    mobile: string;
    full_name: string;
    role: string;
  };
}

/**
 * Creates a tenant through the real backend. Same response envelope
 * shape as getTenant (`{ success, tenant }`), following the existing
 * convention in this file. No backend/API changes made here — this
 * assumes POST /api/tenants already accepts { name, slug } and
 * returns that envelope; adjust the URL/payload/response shape below
 * if the real contract differs.
 */
export async function createTenant(payload: CreateTenantPayload) {
  const response = await apiClient.post('/api/tenants', payload);

  return (response.data as CreateTenantApiResponse).tenant;
}

export interface UpdateTenantPayload {
  name?: string;
  slug?: string;
}

export async function updateTenant(id: string, payload: UpdateTenantPayload) {
  const response = await apiClient.patch(`/api/tenants/${id}`, payload);
  return (response.data as { success: boolean; tenant: TenantApiResponse['tenants'][number] }).tenant;
}

export interface UpdateRepresentativePayload {
  mobile?: string;
  password?: string;
}

export async function updateTenantRepresentative(
  id: string,
  payload: UpdateRepresentativePayload,
) {
  const response = await apiClient.patch(
    `/api/tenants/${id}/representative`,
    payload,
  );
  return (response.data as {
    success: boolean;
    representative: {
      id: number;
      tenant_id: number;
      mobile: string;
      full_name: string | null;
      is_active: number;
    };
  }).representative;
}


export interface DeleteTenantApiResponse {
  success: boolean;
}

/**
 * Deletes a tenant through the real backend. `{ success }` envelope,
 * same convention as the rest of this file. Assumes
 * DELETE /api/tenants/{tenant_id} exists on the backend and returns
 * that envelope — adjust below if the real contract differs.
 */
export async function deleteTenant(id: string) {
  const response = await apiClient.delete(`/api/tenants/${id}`);

  return (response.data as DeleteTenantApiResponse).success;
}


export interface SuspendTenantApiResponse {
    success: boolean;
    tenant: {
        id: number;
        name: string;
        slug: string;
        status: 'active' | 'inactive' | 'suspended';
        metadata?: Record<string, unknown>;
        created_at?: string | null;
        updated_at?: string | null;
    };
}

/**
 * Suspends a tenant through the real backend.
 * PATCH /api/tenants/{tenant_id}/suspend
 */
export async function suspendTenant(id: string) {
    const response = await apiClient.patch(
        `/api/tenants/${id}/suspend`
    );

    return (response.data as SuspendTenantApiResponse).tenant;
}


export interface DeactivateTenantApiResponse {
    success: boolean;
    tenant: {
        id: number;
        name: string;
        slug: string;
        status: 'active' | 'inactive' | 'suspended';
        metadata?: Record<string, unknown>;
        created_at?: string | null;
        updated_at?: string | null;
    };
}

/**
 * Deactivates a tenant through the real backend. Same envelope shape
 * and pattern as suspendTenant — confirmed against routers/tenants.py:
 * PATCH /api/tenants/{tenant_id}/deactivate.
 */
export async function deactivateTenant(id: string) {
    const response = await apiClient.patch(
        `/api/tenants/${id}/deactivate`
    );

    return (response.data as DeactivateTenantApiResponse).tenant;
}


export interface ActivateTenantApiResponse {
    success: boolean;
    tenant: {
        id: number;
        name: string;
        slug: string;
        status: 'active' | 'inactive' | 'suspended';
        metadata?: Record<string, unknown>;
        created_at?: string | null;
        updated_at?: string | null;
    };
}

/**
 * Activates a suspended tenant through the real backend. Reverse of
 * suspendTenant — same envelope shape and pattern, confirmed against
 * routers/tenants.py: PATCH /api/tenants/{tenant_id}/activate.
 */
export async function activateTenant(id: string) {
    const response = await apiClient.patch(
        `/api/tenants/${id}/activate`
    );

    return (response.data as ActivateTenantApiResponse).tenant;
}
